import React from "react";
import { useNavigate } from "react-router-dom";

// ── Types ────────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// ── CredentialRequiredModal ───────────────────────────────────────────────────

/**
 * Blocking modal shown when a user without a platform-verified medical
 * registration (can_prescribe === false) attempts a clinical action —
 * starting a visit, opening the diagnosis pad, recording SOAP notes, etc.
 *
 * Single source of truth so the Patient page's "Start Visit" CTA and the
 * sidebar's "Diagnosis" entry surface the exact same prompt. The backend still
 * returns 403 CLINICAL_CREDENTIAL_REQUIRED as the authoritative backstop.
 */
export const CredentialRequiredModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-[#fffbeb]">
          <svg width="32" height="32" viewBox="0 0 20 20" fill="#ea580c">
            <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-slate-800 text-center mb-2">
          Verify your credentials
        </h3>
        <p className="text-slate-600 text-center mb-6">
          Starting a visit, recording SOAP notes and prescribing require a
          platform-verified medical registration. Complete your credential
          verification to unlock clinical actions.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => navigate("/settings?section=compliance&card=clinic-credentialing")}
            className="flex-1 px-4 py-3 rounded-xl bg-[#ea580c] text-white font-medium hover:bg-orange-700 transition-colors"
          >
            Verify credentials
          </button>
        </div>
      </div>
    </div>
  );
};

export default CredentialRequiredModal;
